import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getLandingPageImages, type LandingPageImage } from '../lib/maison-adji-api';
import { useLanguage } from '../context/LanguageContext';
import { Lock, Mail, Phone, Loader2, ArrowRight, Smartphone, ChevronDown, CheckCircle } from 'lucide-react';
import { countryCodes, defaultCountry, getPhonePlaceholder } from '../lib/country-codes';
import type { CountryCode } from '../lib/country-codes';

function LoginPage() {
  const { t } = useLanguage();
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone' | 'others' | 'whatsapp'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry);
  const [whatsappCountry, setWhatsappCountry] = useState<CountryCode>(defaultCountry);
  const [isSignUp, setIsSignUp] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showWhatsappDropdown, setShowWhatsappDropdown] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [whatsappOtpCode, setWhatsappOtpCode] = useState(['', '', '', '', '', '']);
  const [showOtp, setShowOtp] = useState(false);
  const [showWhatsappOtp, setShowWhatsappOtp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [landingImages, setLandingImages] = useState<LandingPageImage[]>([]);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Charger les images de la landing (shots Maison Adji)
  useEffect(() => {
    getLandingPageImages().then((images) => {
      setLandingImages(images);
      if (images.length > 0) setCarouselIndex(0);
    });
  }, []);

  // Après connexion/inscription : lier la commande au profil si on vient de la page succès checkout (payment_id)
  const linkOrderToProfileIfNeeded = async () => {
    const fromCheckout = searchParams.get('from') === 'checkout_success';
    const paymentId = searchParams.get('payment_id');
    if (fromCheckout && paymentId) {
      try {
        await supabase.rpc('link_order_to_profile', { payment_id_from_provider: paymentId });
      } catch (_) {
        // ignore: la commande n'existe peut-être pas encore (webhook PayLink en retard)
      }
    }
  };

  // Vérifier si l'utilisateur est déjà connecté au chargement de la page
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await linkOrderToProfileIfNeeded();
          const redirect = searchParams.get('redirect') || '/';
          navigate(redirect);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de session:', error);
        setLoading(false);
      }
    };

    checkSession();

    // Écouter les changements de session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        await linkOrderToProfileIfNeeded();
        const redirect = searchParams.get('redirect') || '/';
        navigate(redirect);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, searchParams]);

  // Carousel auto-advance pour les images de la landing
  useEffect(() => {
    if (landingImages.length <= 1) return;
    const t = setInterval(() => setCarouselIndex((i) => (i + 1) % landingImages.length), 4500);
    return () => clearInterval(t);
  }, [landingImages.length]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSignUpSuccess(false);

    try {
      const response = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (response.error) {
        setError(response.error.message || 'Erreur de connexion');
        setLoading(false);
        return;
      }

      if (response.data.user) {
        await linkOrderToProfileIfNeeded();
        const redirect = searchParams.get('redirect') || '/';
        navigate(redirect);
      }
    } catch {
      setError('Une erreur inattendue est survenue');
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSignUpSuccess(false);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${window.location.origin}/${searchParams.get('redirect') || ''}` },
      });

      if (signUpError) {
        setError(signUpError.message || 'Erreur lors de l\'inscription');
        setLoading(false);
        return;
      }

      if (data.user) {
        if (data.session) {
          await linkOrderToProfileIfNeeded();
          const redirect = searchParams.get('redirect') || '/';
          navigate(redirect);
        } else {
          setSignUpSuccess(true);
          setError(null);
        }
      }
      setLoading(false);
    } catch {
      setError('Une erreur inattendue est survenue');
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) {
      setError('Veuillez entrer votre numéro de téléphone');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fullPhoneNumber = `${selectedCountry.dialCode}${phone.replace(/\s/g, '')}`;
      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone: fullPhoneNumber,
      });

      if (otpError) {
        setError(otpError.message || 'Erreur lors de l\'envoi du code');
        setLoading(false);
        return;
      }

      setShowOtp(true);
      setLoading(false);
    } catch {
      setError('Une erreur inattendue est survenue');
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;
    
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value;
    setOtpCode(newOtpCode);
    setError(null);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }

    if (newOtpCode.every(code => code !== '') && newOtpCode.length === 6) {
      handleOtpVerification(newOtpCode.join(''));
    }
  };

  const handleOtpVerification = async (codeValue?: string) => {
    const code = codeValue || otpCode.join('');
    
    if (code.length !== 6) {
      setError('Veuillez saisir les 6 chiffres du code de vérification');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fullPhoneNumber = `${selectedCountry.dialCode}${phone.replace(/\s/g, '')}`;
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        phone: fullPhoneNumber,
        token: code,
        type: 'sms',
      });

      if (verifyError) {
        setError(verifyError.message || 'Code incorrect. Veuillez réessayer.');
        setOtpCode(['', '', '', '', '', '']);
        setLoading(false);
        setTimeout(() => {
          const firstInput = document.getElementById('otp-0');
          firstInput?.focus();
        }, 100);
        return;
      }

      if (data.session) {
        await linkOrderToProfileIfNeeded();
        const redirect = searchParams.get('redirect') || '/';
        navigate(redirect);
      }
    } catch {
      setError('Une erreur inattendue est survenue');
      setLoading(false);
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleWhatsappSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!whatsappPhone) {
      setError('Veuillez entrer votre numéro WhatsApp');
      return;
    }
    setShowWhatsappOtp(true);
    setLoading(false);
  };

  const handleWhatsappOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtpCode = [...whatsappOtpCode];
    newOtpCode[index] = value;
    setWhatsappOtpCode(newOtpCode);

    if (value && index < 5) {
      const nextInput = document.getElementById(`whatsapp-otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleWhatsappOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !whatsappOtpCode[index] && index > 0) {
      const prevInput = document.getElementById(`whatsapp-otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  useEffect(() => {
    if (showOtp) {
      setTimeout(() => {
        const firstInput = document.getElementById('otp-0');
        firstInput?.focus();
      }, 100);
    }
  }, [showOtp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-country-dropdown]')) {
        setShowCountryDropdown(false);
      }
      if (!target.closest('[data-whatsapp-dropdown]')) {
        setShowWhatsappDropdown(false);
      }
    };

    if (showCountryDropdown || showWhatsappDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCountryDropdown, showWhatsappDropdown]);

  const displayImages = landingImages.length > 0 ? landingImages : [];
  const fallbackImage = 'https://images.unsplash.com/photo-1558769132-cb1aea913ec4?w=800&q=80';

  if (loading && !showOtp && !showWhatsappOtp) {
    return (
      <div className="relative min-h-screen bg-neutral-950 flex items-center justify-center">
        <div
          className="absolute inset-0 opacity-[0.035] bg-repeat"
          style={{ backgroundImage: 'url(/assets/images/maisonadjiwhite.png)', backgroundSize: '72px 72px' }}
          aria-hidden
        />
        <Loader2 className="relative z-10 w-8 h-8 text-white/80 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-neutral-950 flex items-center justify-center p-4 md:p-2 overflow-x-hidden">
      {/* Fond motif marque Maison Adji (style monogramme répété) */}
      <div
        className="absolute inset-0 opacity-[0.035] bg-repeat pointer-events-none"
        style={{ backgroundImage: 'url(/assets/images/maisonadjiwhite.png)', backgroundSize: '72px 72px' }}
        aria-hidden
      />
      <div className="relative z-10 w-full max-w-6xl overflow-x-hidden">
        <div className="grid gap-8 md:gap-6 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          {/* Panneau gauche : images de la landing (shots Maison Adji) — min-w-0 évite que le bandeau défilant n'élargisse la colonne */}
          <div className="hidden md:flex md:flex-col md:items-center md:justify-center md:order-1 min-w-0 overflow-hidden">
            <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 aspect-[4/5] max-h-[560px] bg-neutral-900">
              {displayImages.length > 0 ? (
                <>
                  {displayImages.map((img, index) => (
                    <img
                      key={img.id}
                      src={img.url}
                      alt={img.alt_text || 'Maison Adji'}
                      className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
                        index === carouselIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                      }`}
                    />
                  ))}
                  {displayImages.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 z-30 flex gap-2 -translate-x-1/2">
                      {displayImages.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setCarouselIndex(i)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            i === carouselIndex ? 'bg-white' : 'bg-white/40'
                          }`}
                          aria-label={`Image ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <img
                  src={fallbackImage}
                  alt="Maison Adji"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              )}
              {/* Overlay : logo + texte Maison Adji */}
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gradient-to-t from-black/70 via-black/30 to-transparent p-8 text-center">
                <img
                  src="/assets/images/maisonadjiwhite.png"
                  alt="Maison Adji"
                  className="mb-4 h-14 md:h-20 w-auto object-contain opacity-95"
                />
                <p className="font-['Sora'] text-xs md:text-sm tracking-[0.35em] text-white/90 uppercase max-w-[280px]">
                  {t('home.subtitle')}
                </p>
                <p className="mt-2 font-['Rubik'] text-sm text-white/80 max-w-[260px] leading-relaxed">
                  {t('home.tagline')}
                </p>
              </div>
            </div>
            {/* Bandeau défilant d'images - Desktop */}
            <div className="hidden md:block w-full overflow-x-hidden mt-4">
              <div className="relative w-full overflow-hidden">
                <div className="flex gap-3 animate-scroll-left">
                  {displayImages.length > 0
                    ? [...displayImages, ...displayImages].map((img, index) => (
                        <div
                          key={`${img.id}-${index}`}
                          className="shrink-0 aspect-square rounded-xl overflow-hidden border border-white/20 shadow-lg"
                          style={{ width: 'calc((100% - 1rem) / 3)', minWidth: 'calc((100% - 1rem) / 3)' }}
                        >
                          <img
                            src={img.url}
                            alt={img.alt_text || 'Maison Adji'}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ))
                    : [...Array(6), ...Array(6)].map((_, i) => (
                        <div
                          key={`fb-${i}`}
                          className="shrink-0 aspect-square rounded-xl overflow-hidden border border-white/20 shadow-lg"
                          style={{ width: 'calc((100% - 1rem) / 3)', minWidth: 'calc((100% - 1rem) / 3)' }}
                        >
                          <img src={fallbackImage} alt="Maison Adji" className="w-full h-full object-cover" />
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire de connexion */}
          <div className="min-w-0 p-8 bg-white rounded-2xl shadow-2xl order-1 md:order-2 overflow-x-hidden">
            <div className="flex justify-center mb-6">
              <img
                src="/assets/images/maisonadjiblack.png"
                alt="Maison Adji"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </div>

            {/* Mini bandeau images - Mobile uniquement */}
            {displayImages.length > 0 && (
              <div className="mb-6 md:hidden w-full overflow-x-hidden">
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {displayImages.slice(0, 6).map((img) => (
                    <div key={img.id} className="shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-neutral-200">
                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h1 className="mb-2 text-3xl font-bold text-center text-neutral-900 font-['Sora']">
              {isSignUp ? t('login.signup_title') : t('login.title')}
            </h1>
            <p className="mb-8 text-center text-neutral-600">
              {t('login.subtitle')}
            </p>
            {signUpSuccess && (
              <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm text-center">
                {t('login.check_email')}
              </div>
            )}

            {/* Toggle Method */}
            <div className="flex gap-1 p-1 mb-6 bg-neutral-100 rounded-xl">
              <button
                onClick={() => setLoginMethod('email')}
                className={`flex-1 py-3 px-2 rounded-lg font-semibold transition-all duration-300 ${
                  loginMethod === 'email'
                    ? 'bg-neutral-900 text-white shadow-lg'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Mail className="inline-block mr-1 w-5 h-5 md:mr-2" />
                <span className="hidden sm:inline">{t('login.email')}</span>
              </button>
              <button
                onClick={() => setLoginMethod('phone')}
                className={`flex-1 py-3 px-2 rounded-lg font-semibold transition-all duration-300 ${
                  loginMethod === 'phone'
                    ? 'bg-neutral-900 text-white shadow-lg'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Phone className="inline-block mr-1 w-5 h-5 md:mr-2" />
                <span className="hidden sm:inline">{t('login.phone')}</span>
              </button>
              <button
                onClick={() => setLoginMethod('others')}
                className={`flex-1 py-3 px-2 rounded-lg font-semibold transition-all duration-300 ${
                  loginMethod === 'others'
                    ? 'bg-neutral-900 text-white shadow-lg'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Smartphone className="inline-block mr-1 w-5 h-5 md:mr-2" />
                <span className="hidden sm:inline">{t('login.others')}</span>
              </button>
            </div>

            {/* Others Login Methods */}
            {loginMethod === 'others' ? (
              <div className="space-y-3">
                <button
                  onClick={() => setLoginMethod('whatsapp')}
                  className="flex items-center p-4 w-full bg-white rounded-xl border-2 border-neutral-200 transition-all duration-300 hover:border-neutral-900 hover:shadow-lg group"
                >
                  <div className="flex justify-center items-center mr-4 w-12 h-12 bg-white rounded-lg border border-gray-200 transition-transform group-hover:scale-110 overflow-hidden">
                    <img
                      src="/assets/images/whatsapplogo.png"
                      alt="WhatsApp"
                      className="object-contain w-full h-full p-1"
                      onError={(e) => {
                        console.error('Error loading WhatsApp logo');
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <span className="font-semibold text-gray-900">{t('login.continue_whatsapp')}</span>
                </button>

                <button
                  onClick={() => console.log('Google login')}
                  className="flex items-center p-4 w-full bg-white rounded-xl border-2 border-neutral-200 transition-all duration-300 hover:border-neutral-900 hover:shadow-lg group"
                >
                  <div className="flex justify-center items-center mr-4 w-12 h-12 bg-white rounded-lg border border-gray-200 transition-transform group-hover:scale-110 overflow-hidden">
                    <img
                      src="/assets/images/Google-Symbol.png"
                      alt="Google"
                      className="object-contain w-full h-full p-1"
                      onError={(e) => {
                        console.error('Error loading Google logo');
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <span className="font-semibold text-gray-900">{t('login.continue_google')}</span>
                </button>
              </div>
            ) : loginMethod === 'phone' ? (
              <form onSubmit={handlePhoneSubmit} className="space-y-4">
                {!showOtp ? (
                  <>
                    <div className="flex gap-2" data-country-dropdown>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                          className="flex justify-center items-center px-3 h-12 md:h-14 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all duration-300 hover:border-neutral-900"
                        >
                          <img
                            src={`https://flagcdn.com/w40/${selectedCountry.flag.toLowerCase()}.png`}
                            alt={selectedCountry.name}
                            className="object-cover w-8 h-6 rounded"
                          />
                          <ChevronDown className="ml-2 w-4 h-4 text-gray-600" />
                        </button>

                        {showCountryDropdown && (
                          <div className="overflow-y-auto absolute left-0 top-full z-50 mt-2 w-72 max-h-60 bg-white rounded-xl border-2 border-gray-200 shadow-xl">
                            {countryCodes.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setSelectedCountry(country);
                                  setShowCountryDropdown(false);
                                }}
                                className="flex items-center p-3 w-full transition-colors duration-200 hover:bg-neutral-900/10"
                              >
                                <img
                                  src={`https://flagcdn.com/w40/${country.flag.toLowerCase()}.png`}
                                  alt={country.name}
                                  className="object-cover mr-3 w-8 h-6 rounded"
                                />
                                <span className="flex-1 font-medium text-left text-gray-900">{country.name}</span>
                                <span className="text-gray-500">{country.dialCode}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 items-center rounded-xl border-2 border-gray-200 transition-all duration-300 focus-within:border-neutral-900">
                        <span className="flex items-center px-3 md:px-4 h-12 md:h-14 text-sm md:text-base font-medium text-gray-700 bg-gray-50 rounded-l-xl border-r border-gray-200">
                          {selectedCountry.dialCode}
                        </span>
                        <div className="relative flex-1">
                          <Phone className="absolute left-3 top-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 transform -translate-y-1/2" />
                          <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder={getPhonePlaceholder(selectedCountry.code)}
                            required
                            className="py-3 md:py-4 pr-3 md:pr-4 pl-10 md:pl-12 w-full text-neutral-900 text-sm md:text-base rounded-r-xl border-0 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="p-4 text-red-600 bg-red-50 rounded-xl border-2 border-red-200">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-neutral-900 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          {t('login.sending')}
                        </>
                      ) : (
                        <>
                          {t('login.send_code')}
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="space-y-4">
                      <p className="text-center text-gray-600">
                        {t('login.otp_prompt')}
                      </p>
                      <div className="flex gap-3 justify-center">
                        {otpCode.map((digit, index) => (
                          <input
                            key={index}
                            id={`otp-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              handleOtpChange(index, value);
                            }}
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            aria-label={`Chiffre ${index + 1} du code OTP`}
                            title={`Chiffre ${index + 1}`}
                            autoFocus={index === 0 && showOtp}
                            className="w-12 h-14 text-2xl font-bold text-center text-neutral-900 rounded-xl border-2 border-gray-200 transition-all duration-300 focus:outline-none focus:border-neutral-900"
                          />
                        ))}
                      </div>
                    </div>

                    {error && (
                      <div className="p-4 text-red-600 bg-red-50 rounded-xl border-2 border-red-200">
                        {error}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleOtpVerification()}
                      disabled={loading || otpCode.some(code => code === '')}
                      className="w-full bg-neutral-900 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          {t('login.verifying')}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 w-5 h-5" />
                          {t('login.verify')}
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        setOtpCode(['', '', '', '', '', '']);
                        setError(null);
                        setLoading(true);
                        try {
                          const fullPhoneNumber = `${selectedCountry.dialCode}${phone.replace(/\s/g, '')}`;
                          const { error: resendError } = await supabase.auth.signInWithOtp({
                            phone: fullPhoneNumber,
                          });
                          
                          if (resendError) {
                            setError(resendError.message || 'Erreur lors du renvoi du code');
                          }
                        } catch {
                          setError('Erreur lors du renvoi du code');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full text-sm font-medium text-neutral-900 hover:text-neutral-700 disabled:opacity-50"
                    >
                      {t('login.resend')}
                    </button>
                  </>
                )}
              </form>
            ) : loginMethod === 'whatsapp' ? (
              <form onSubmit={handleWhatsappSubmit} className="space-y-4">
                {!showWhatsappOtp ? (
                  <>
                    <div className="flex gap-2" data-whatsapp-dropdown>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowWhatsappDropdown(!showWhatsappDropdown)}
                          className="flex justify-center items-center px-3 h-12 md:h-14 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all duration-300 hover:border-neutral-900"
                        >
                          <img
                            src={`https://flagcdn.com/w40/${whatsappCountry.flag.toLowerCase()}.png`}
                            alt={whatsappCountry.name}
                            className="object-cover w-8 h-6 rounded"
                          />
                          <ChevronDown className="ml-2 w-4 h-4 text-gray-600" />
                        </button>

                        {showWhatsappDropdown && (
                          <div className="overflow-y-auto absolute left-0 top-full z-50 mt-2 w-72 max-h-60 bg-white rounded-xl border-2 border-gray-200 shadow-xl">
                            {countryCodes.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setWhatsappCountry(country);
                                  setShowWhatsappDropdown(false);
                                }}
                                className="flex items-center p-3 w-full transition-colors duration-200 hover:bg-neutral-900/10"
                              >
                                <img
                                  src={`https://flagcdn.com/w40/${country.flag.toLowerCase()}.png`}
                                  alt={country.name}
                                  className="object-cover mr-3 w-8 h-6 rounded"
                                />
                                <span className="flex-1 font-medium text-left text-gray-900">{country.name}</span>
                                <span className="text-gray-500">{country.dialCode}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 items-center rounded-xl border-2 border-gray-200 transition-all duration-300 focus-within:border-neutral-900">
                        <span className="flex items-center px-3 md:px-4 h-12 md:h-14 text-sm md:text-base font-medium text-gray-700 bg-gray-50 rounded-l-xl border-r border-gray-200">
                          {whatsappCountry.dialCode}
                        </span>
                        <div className="relative flex-1">
                          <img
                            src="/assets/images/whatsapplogo.png"
                            alt="WhatsApp"
                            className="absolute left-3 top-1/2 w-4 h-4 md:w-5 md:h-5 transform -translate-y-1/2 object-contain"
                            onError={(e) => {
                              console.error('Error loading WhatsApp logo in input');
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          <input
                            type="tel"
                            value={whatsappPhone}
                            onChange={(e) => setWhatsappPhone(e.target.value)}
                            placeholder={getPhonePlaceholder(whatsappCountry.code)}
                            required
                            className="py-3 md:py-4 pr-3 md:pr-4 pl-10 md:pl-12 w-full text-neutral-900 text-sm md:text-base rounded-r-xl border-0 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="p-4 text-red-600 bg-red-50 rounded-xl border-2 border-red-200">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-neutral-900 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          {t('login.sending')}
                        </>
                      ) : (
                        <>
                          {t('login.send_code')}
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="space-y-4">
                      <div className="flex justify-center items-center mb-2">
                        <img
                          src="/assets/images/whatsapplogo.png"
                          alt="WhatsApp"
                          className="mr-3 w-8 h-8 object-contain"
                          onError={(e) => {
                            console.error('Error loading WhatsApp logo in OTP section');
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                        <p className="font-semibold text-center text-gray-600">
                          {t('login.whatsapp_code')}
                        </p>
                      </div>
                      <p className="text-sm text-center text-gray-600">
                        {t('login.otp_prompt')}
                      </p>
                      <div className="flex gap-3 justify-center">
                        {whatsappOtpCode.map((digit, index) => (
                          <input
                            key={index}
                            id={`whatsapp-otp-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              handleWhatsappOtpChange(index, value);
                            }}
                            onKeyDown={(e) => handleWhatsappOtpKeyDown(index, e)}
                            aria-label={`Chiffre ${index + 1} du code OTP WhatsApp`}
                            title={`Chiffre ${index + 1}`}
                            className="w-12 h-14 text-2xl font-bold text-center text-neutral-900 rounded-xl border-2 border-gray-200 transition-all duration-300 focus:outline-none focus:border-neutral-900"
                          />
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setWhatsappOtpCode(['', '', '', '', '', '']);
                        setShowWhatsappOtp(false);
                      }}
                      className="w-full text-sm font-medium text-neutral-900 hover:text-neutral-700"
                    >
                      {t('login.resend')}
                    </button>
                  </>
                )}
              </form>
            ) : (
              <form onSubmit={isSignUp ? handleSignUp : handleLogin} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.placeholder_email')}
                    required
                    className="py-4 pr-4 pl-12 w-full text-neutral-900 rounded-xl border-2 border-gray-200 transition-all duration-300 focus:outline-none focus:border-neutral-900"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.placeholder_password')}
                    required
                    minLength={6}
                    className="py-4 pr-4 pl-12 w-full text-neutral-900 rounded-xl border-2 border-gray-200 transition-all duration-300 focus:outline-none focus:border-neutral-900"
                  />
                </div>

                {error && (
                  <div className="p-4 text-red-600 bg-red-50 rounded-xl border-2 border-red-200">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-neutral-900 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      {isSignUp ? t('login.signup_loading') : t('login.loading')}
                    </>
                  ) : (
                    <>
                      {isSignUp ? t('login.signup_submit') : t('login.submit')}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}

            <div className="mt-6 space-y-3 text-center">
              {loginMethod === 'email' && !isSignUp && (
                <a href="#" className="block text-sm font-medium text-neutral-900 hover:text-neutral-700">
                  {t('login.forgot')}
                </a>
              )}
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError(null);
                  setSignUpSuccess(false);
                }}
                className="block w-full text-sm font-medium text-neutral-600 hover:text-neutral-900"
              >
                {isSignUp ? t('login.switch_to_login') : t('login.switch_to_signup')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;

